// ─────────────────────────────────────────────────────────────────────────
// Hearth — typed REST client. All paths are same-origin relative (/api/...),
// proxied to the FastAPI backend by Vite in dev and served directly in prod.
// ─────────────────────────────────────────────────────────────────────────
import type {
  AbilityArrayResponse,
  Advantage,
  AuthUser,
  BuildCharacterRequest,
  ClassSpellsResponse,
  CompendiumDetail,
  CompendiumKind,
  CompendiumListResponse,
  CreateGameResponse,
  DmStyle,
  FullCharacter,
  FullGame,
  GamesListResponse,
  HealthResponse,
  LogResponse,
  LevelUpOptions,
  OptionDetail,
  ClassProgression,
  EditCharacterPatch,
  RebuildPatch,
  Quest,
  LevelUpRequest,
  LevelUpResult,
  PointBuyResponse,
  RestRequest,
  RestResult,
  CharacterActionResult,
  CombatOptions,
  CombatFeaturesResponse,
  RollResult,
  Scores,
  SeatAssignResponse,
  SrdMeta,
} from "./types";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "ApiError";
  }
}

// ── Session token (set by the auth layer; attached to every request) ──
const TOKEN_KEY = "hearth_token";
let authToken: string | null =
  typeof localStorage !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;

export function setAuthToken(token: string | null): void {
  authToken = token;
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* localStorage unavailable — token stays in-memory */
  }
}

export function getAuthToken(): string | null {
  return authToken;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        ...(init?.headers ?? {}),
      },
    });
  } catch (e) {
    throw new ApiError(0, `Network error — is the Hearth backend running? (${String(e)})`);
  }
  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }
  if (!res.ok) {
    const msg =
      (body && typeof body === "object" && "detail" in body
        ? String((body as Record<string, unknown>).detail)
        : null) ||
      (typeof body === "string" ? body : null) ||
      `${res.status} ${res.statusText}`;
    throw new ApiError(res.status, msg);
  }
  return body as T;
}

const qs = (params: Record<string, string | number | undefined | null>): string => {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
};

export const api = {
  // ── Auth ──
  register: (username: string, password: string, display_name?: string) =>
    request<{ token: string; user: AuthUser }>("/api/auth/register", {
      method: "POST",
      body: JSON.stringify({ username, password, display_name }),
    }),
  login: (username: string, password: string) =>
    request<{ token: string; user: AuthUser }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),
  logout: () => request<{ ok: boolean }>("/api/auth/logout", { method: "POST" }),
  me: () => request<{ user: AuthUser | null }>("/api/auth/me"),

  // ── Health & SRD ──
  health: () => request<HealthResponse>("/api/health"),
  meta: () => request<SrdMeta>("/api/srd/meta"),
  feats: () => request<{ feats: { index: string; name: string; desc: string; category: string; grants_skills: number; grants_spells: boolean }[] }>("/api/feats"),
  detail: (kind: string, index: string) =>
    request<OptionDetail>(`/api/srd/detail/${kind}/${index}`),
  classProgression: (classIndex: string) =>
    request<ClassProgression>(`/api/srd/class/${classIndex}/progression`),
  classSpells: (classIndex: string) =>
    request<ClassSpellsResponse>(`/api/srd/class/${classIndex}/spells`),

  // ── Dice ──
  roll: (expression: string, advantage: Advantage = "normal") =>
    request<RollResult>("/api/roll", {
      method: "POST",
      body: JSON.stringify({ expression, advantage }),
    }),
  abilityArray: () => request<AbilityArrayResponse>("/api/roll/ability-array"),

  // ── Character builder ──
  previewCharacter: (req: BuildCharacterRequest) =>
    request<FullCharacter>("/api/character/preview", {
      method: "POST",
      body: JSON.stringify(req),
    }),
  validatePointBuy: (scores: Scores) =>
    request<PointBuyResponse>("/api/validate/point-buy", {
      method: "POST",
      body: JSON.stringify(scores),
    }),

  // ── Games & seats ──
  games: () => request<GamesListResponse>("/api/games"),
  createGame: (body: {
    name: string;
    setting?: string;
    dm_style?: DmStyle;
    tone_notes?: string;
    scene?: string;
    num_seats?: number;
    visibility?: "public" | "private";
  }) =>
    request<CreateGameResponse>("/api/games", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  game: (id: string) => request<FullGame>(`/api/games/${id}`),
  art: (body: { kind: "character" | "item" | "spell" | "scene"; game_id?: string; character_id?: string; name?: string; item_kind?: string; school?: string }) =>
    request<{ kind: string; name: string | null; character_id: string | null; ready: boolean; url: string | null; pending?: boolean }>("/api/art", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deleteGame: (id: string) =>
    request<{ deleted: boolean }>(`/api/games/${id}`, { method: "DELETE" }),
  restartGame: (id: string) =>
    request<{ restarted: boolean; game: FullGame }>(`/api/games/${id}/restart`, { method: "POST" }),
  editCharacter: (gameId: string, characterId: string, patch: EditCharacterPatch) =>
    request<{ character: FullCharacter }>(`/api/games/${gameId}/character/${characterId}/edit`, {
      method: "POST",
      body: JSON.stringify(patch),
    }),
  rebuildCharacter: (gameId: string, characterId: string, patch: RebuildPatch) =>
    request<{ character: FullCharacter; changed: string[] }>(`/api/games/${gameId}/character/${characterId}/rebuild`, {
      method: "POST",
      body: JSON.stringify(patch),
    }),
  claimSeatForUser: (gameId: string, seatId: string) =>
    request<{ seat: string; claimed_by: AuthUser }>(`/api/games/${gameId}/claim`, {
      method: "POST",
      body: JSON.stringify({ seat_id: seatId }),
    }),
  releaseSeat: (gameId: string, seatId: string) =>
    request<{ released: string }>(`/api/games/${gameId}/release`, {
      method: "POST",
      body: JSON.stringify({ seat_id: seatId }),
    }),
  assignSeat: (
    gameId: string,
    body: {
      seat_id: string;
      controller_kind: "human" | "ai";
      label?: string;
      persona?: string;
      character: BuildCharacterRequest;
    },
  ) =>
    request<SeatAssignResponse>(`/api/games/${gameId}/seat`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  clearSeat: (gameId: string, seatId: string) =>
    request<{ cleared: boolean }>(`/api/games/${gameId}/seat/${seatId}/clear`, {
      method: "POST",
    }),
  addSeat: (gameId: string) =>
    request<{ added: string; game: FullGame }>(`/api/games/${gameId}/seat/add`, {
      method: "POST",
    }),
  character: (gameId: string, characterId: string) =>
    request<FullCharacter>(`/api/games/${gameId}/character/${characterId}`),
  log: (gameId: string, offset = 0, limit = 200) =>
    request<LogResponse>(`/api/games/${gameId}/log${qs({ offset, limit })}`),

  // ── quest journal ──
  addQuest: (gameId: string, text: string, desc = "") =>
    request<{ added: string; quests: Quest[] }>(`/api/games/${gameId}/quests`, {
      method: "POST",
      body: JSON.stringify({ text, desc }),
    }),
  editQuest: (gameId: string, questId: string, patch: { text?: string; desc?: string; complete?: boolean }) =>
    request<{ updated: string; quests: Quest[] }>(`/api/games/${gameId}/quests/${questId}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  deleteQuest: (gameId: string, questId: string) =>
    request<{ deleted: string; quests: Quest[] }>(`/api/games/${gameId}/quests/${questId}`, {
      method: "DELETE",
    }),

  // ── progression & rest ──
  levelUpOptions: (gameId: string, characterId: string, classIndex: string) =>
    request<LevelUpOptions>(`/api/games/${gameId}/character/${characterId}/levelup/${classIndex}`),
  levelUp: (gameId: string, characterId: string, body: LevelUpRequest) =>
    request<LevelUpResult>(`/api/games/${gameId}/character/${characterId}/levelup`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  shortRest: (gameId: string, body: RestRequest = {}) =>
    request<RestResult>(`/api/games/${gameId}/rest/short`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  longRest: (gameId: string, body: RestRequest = {}) =>
    request<RestResult>(`/api/games/${gameId}/rest/long`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // ── inventory & in-play actions ──
  addItem: (gameId: string, characterId: string, body: { index: string; name?: string; quantity?: number; kind?: string; base?: string }) =>
    request<CharacterActionResult>(`/api/games/${gameId}/character/${characterId}/inventory/add`, {
      method: "POST", body: JSON.stringify(body),
    }),
  attune: (gameId: string, characterId: string, body: { index: string; name?: string; attuned: boolean }) =>
    request<CharacterActionResult>(`/api/games/${gameId}/character/${characterId}/attune`, {
      method: "POST", body: JSON.stringify(body),
    }),
  removeItem: (gameId: string, characterId: string, body: { index: string; name?: string; quantity?: number }) =>
    request<CharacterActionResult>(`/api/games/${gameId}/character/${characterId}/inventory/remove`, {
      method: "POST", body: JSON.stringify(body),
    }),
  equip: (gameId: string, characterId: string, body: { index: string; name?: string; equipped: boolean }) =>
    request<CharacterActionResult>(`/api/games/${gameId}/character/${characterId}/equip`, {
      method: "POST", body: JSON.stringify(body),
    }),
  useItem: (gameId: string, characterId: string, body: { index: string; name?: string }) =>
    request<CharacterActionResult>(`/api/games/${gameId}/character/${characterId}/use-item`, {
      method: "POST", body: JSON.stringify(body),
    }),
  cast: (gameId: string, characterId: string, body: { spell_index: string; slot_level?: number }) =>
    request<CharacterActionResult>(`/api/games/${gameId}/character/${characterId}/cast`, {
      method: "POST", body: JSON.stringify(body),
    }),
  restoreSlot: (gameId: string, characterId: string, level: number) =>
    request<CharacterActionResult>(`/api/games/${gameId}/character/${characterId}/slot/restore`, {
      method: "POST", body: JSON.stringify({ level }),
    }),
  combatOptions: (gameId: string, characterId: string) =>
    request<CombatOptions>(`/api/games/${gameId}/character/${characterId}/combat-options`),
  features: (gameId: string, characterId: string) =>
    request<CombatFeaturesResponse>(`/api/games/${gameId}/character/${characterId}/features`),

  // ── Compendium ──
  compendium: (
    kind: CompendiumKind,
    params: {
      q?: string;
      level?: number;
      klass?: string;
      cr_min?: number;
      cr_max?: number;
      limit?: number;
    } = {},
  ) => request<CompendiumListResponse>(`/api/compendium/${kind}${qs(params)}`),
  compendiumDetail: (kind: CompendiumKind, index: string) =>
    request<CompendiumDetail>(`/api/compendium/${kind}/${index}`),
};
